import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import { Button, SelectComponent, Textarea, Checkbox } from "shared";
import { Input, DropFileInput } from "shared";
import RadioGroup from "../../AddInvestmentModal/fields/RadioGroup";
import ComputedField from "../../AddInvestmentModal/fields/ComputedField";
import { InvestmentItem } from "../../../types";
import { RealEstateValues } from "./types";
import { realEstateSchema } from "./validationSchemas";
import { PROPERTY_TYPE_OPTIONS, RELIANCE_CONSENT_LABEL } from "./constants";

interface RealEstateFormProps {
  item: InvestmentItem;
  onSubmit: (values: RealEstateValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const RealEstateForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: RealEstateFormProps) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: RealEstateValues = {
    propertyType: String(item.details?.propertyType ?? ""),
    propertyTypeOther: String(item.details?.propertyTypeOther ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    area:
      item.details?.area !== null && item.details?.area !== undefined
        ? String(item.details.area)
        : "",
    city: String(item.details?.city ?? ""),
    address: String(item.details?.address ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={realEstateSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => {
        const price = Number(formik.values.primaryValue);
        const area = Number(formik.values.area);
        const pricePerM2 =
          price > 0 && area > 0
            ? (price / area).toLocaleString("pl-PL", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : "—";

        const currencyLabel =
          currencyOptions.find(
            (o: any) => o.value === formik.values.currencyUuid,
          )?.label ?? "";

        return (
          <Form>
            <ScrollToFirstError />
            <div className="modal-form__fields">
              <RadioGroup
                name="propertyType"
                label=""
                options={PROPERTY_TYPE_OPTIONS}
                value={formik.values.propertyType}
                onChange={(val) => formik.setFieldValue("propertyType", val)}
                error={
                  (formik.touched.propertyType || formik.submitCount > 0) &&
                  formik.errors.propertyType
                    ? formik.errors.propertyType
                    : undefined
                }
              />

              {formik.values.propertyType === "other" && (
                <Field
                  as={Input}
                  name="propertyTypeOther"
                  label="Nazwa"
                  type="text"
                  maxLength={100}
                  error={
                    (formik.touched.propertyTypeOther ||
                      formik.submitCount > 0) &&
                    formik.errors.propertyTypeOther
                      ? formik.errors.propertyTypeOther
                      : undefined
                  }
                />
              )}

              <Field
                as={Input}
                name="city"
                label="Miejscowość"
                maxLength={30}
                type="text"
                error={
                  (formik.touched.city || formik.submitCount > 0) &&
                  formik.errors.city
                    ? formik.errors.city
                    : undefined
                }
              />

              <Field
                as={Input}
                name="address"
                label="Adres"
                type="text"
                maxLength={30}
                error={
                  (formik.touched.address || formik.submitCount > 0) &&
                  formik.errors.address
                    ? formik.errors.address
                    : undefined
                }
              />

              <Field
                as={Input}
                name="primaryValue"
                label="Cena zakupu"
                type="number"
                step="any"
                precision={2}
                maxLength={12}
                data-suffix={currencyLabel}
                error={
                  (formik.touched.primaryValue || formik.submitCount > 0) &&
                  formik.errors.primaryValue
                    ? formik.errors.primaryValue
                    : undefined
                }
              />

              <Field
                as={SelectComponent}
                name="currencyUuid"
                label="Waluta"
                options={currencyOptions}
                isSearchable={true}
                error={
                  (formik.touched.currencyUuid || formik.submitCount > 0) &&
                  formik.errors.currencyUuid
                    ? formik.errors.currencyUuid
                    : undefined
                }
              />

              <Field
                as={Input}
                name="area"
                label="Powierzchnia [w m²]"
                type="number"
                step="any"
                precision={2}
                maxLength={8}
                optional
                error={
                  (formik.touched.area || formik.submitCount > 0) &&
                  formik.errors.area
                    ? formik.errors.area
                    : undefined
                }
              />

              <ComputedField
                label="Cena za m²:"
                value={pricePerM2}
                inline
                suffix={currencyLabel}
              />

              <Field
                component={Textarea}
                name="additionalInfo"
                label="Dodatkowe informacje"
                optional
              />

              <Field
                label="Zdjęcia"
                name="photoFiles"
                id="photoFiles"
                as={DropFileInput}
                accept=".png,.jpg,.jpeg"
                multiple={10}
                maxSize={5}
                description="Maksymalnie 10 plików, maks. 5 MB/plik (JPG, PNG)"
                optional
                showPreview
                existingFiles={
                  existingPhotos.length > 0
                    ? existingPhotos
                        .filter((f: any) => f?.file?.name && f?.file?.url)
                        .map((f: any) => ({
                          name: f.file.name,
                          url: f.file.url,
                        }))
                    : undefined
                }
                onServerFileRemoved={onRemovedPhoto}
              />

              <Field
                label="Umowa"
                name="contractFile"
                id="contractFile"
                as={DropFileInput}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                optional
                multiple={10}
                maxSize={5}
                description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, PNG)"
                existingFiles={existingContractFiles.map((f: any) => ({
                  name: f.file?.name || "Plik",
                  url: f.file?.url || "",
                }))}
                onServerFileRemoved={onRemovedContract}
              />

              <Field
                as={Checkbox}
                name="relianceCanView"
                label={RELIANCE_CONSENT_LABEL}
                checked={formik.values.relianceCanView}
                onChange={() =>
                  formik.setFieldValue(
                    "relianceCanView",
                    !formik.values.relianceCanView,
                  )
                }
              />
            </div>

            <div className="modal-buttons">
              <Button
                type="button"
                className="button--stroke button--rounded"
                onClick={onCancel}
                label="Anuluj"
              />
              <Button
                type="submit"
                className="button--black button--rounded"
                onClick={() => {}}
                label={isPending ? "Zapisywanie..." : "Zapisz"}
                isLoading={isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RealEstateForm;
